import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Instagram, Facebook, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { BRAND_MARK, DowakumMark } from '@/components/BrandDecor';

export default function Contact() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative text-white overflow-hidden"
      style={{ background: '#10092E' }}
    >
      <div className="absolute inset-0 dwk-noise" aria-hidden="true" />
      <div className="absolute top-[-10%] left-[10%] w-[700px] h-[700px] rounded-full blur-[120px]"
           style={{ background: 'radial-gradient(closest-side, rgba(137,72,195,0.35), transparent)' }} aria-hidden="true" />
      <div className="absolute bottom-[-15%] right-[-5%] w-[700px] h-[700px] rounded-full blur-[130px]"
           style={{ background: 'radial-gradient(closest-side, rgba(0,92,255,0.28), transparent)' }} aria-hidden="true" />

      {/* Dowakum brand mark — clean, standalone, no background/glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none hidden md:block absolute left-[-80px] top-1/2 -translate-y-1/2"
      >
        <DowakumMark size={520} style={{ opacity: 1, mixBlendMode: 'normal' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-28 md:py-40">
        {/* Editorial heading */}
        <div className="mb-16 md:mb-24">
          <div className="text-[14px] md:text-[15px] tracking-[0.32em] uppercase text-[#40D3BE] mb-6 font-semibold" data-testid="contact-eyebrow">
            {t('contact.eyebrow')}
          </div>
          <h2
            className={`font-bold tracking-tight leading-[0.98] text-white ${
              isAr ? 'font-ar text-6xl md:text-[132px]' : 'font-en text-6xl md:text-[132px]'
            }`}
            data-testid="contact-heading"
          >
            <span className="dwk-gradient-text-shine">{t('contact.heading')}</span>
          </h2>
          <p className={`mt-6 max-w-xl text-white/65 leading-relaxed ${isAr ? 'font-ar text-base md:text-lg' : 'font-en text-base md:text-lg'}`}>
            {t('contact.subheading')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl">
          <InfoCard
            icon={<Mail size={18} />}
            label={t('contact.info_email')}
            value={t('contact.placeholder_email')}
            href="mailto:info@dowakum.com"
            testId="info-email"
            isAr={isAr}
          />
          <InfoCard
            icon={<MapPin size={18} />}
            label={t('contact.info_address')}
            value={t('contact.placeholder_address')}
            testId="info-address"
            isAr={isAr}
          />
          <SocialCard
            icon={<Instagram size={20} />}
            label="Instagram"
            href="https://www.instagram.com/dowakum.iraq/"
            testId="social-instagram"
            grad="linear-gradient(135deg, #8948C3 0%, #005CFF 100%)"
          />
          <SocialCard
            icon={<Facebook size={20} />}
            label="Facebook"
            href="https://www.facebook.com/profile.php?id=100083034836178"
            testId="social-facebook"
            grad="linear-gradient(135deg, #005CFF 0%, #40D3BE 100%)"
          />
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon, label, value, href, testId, isAr }) {
  const Comp = href ? 'a' : 'div';
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
    >
      <Comp
        {...(href ? { href } : {})}
        data-testid={testId}
        className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/5 backdrop-blur px-6 py-6 hover:border-[#40D3BE]/60 transition-colors h-full"
      >
        <span className="inline-flex items-center justify-center w-12 h-12 rounded-full text-white shrink-0"
              style={{ background: 'linear-gradient(135deg, #8948C3, #005CFF 60%, #40D3BE)' }}>
          {icon}
        </span>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55 mb-1">{label}</div>
          <div className={`text-white/95 truncate ${isAr ? 'font-ar' : 'font-en'}`}>{value}</div>
        </div>
      </Comp>
    </motion.div>
  );
}

function SocialCard({ icon, label, href, testId, grad }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="group relative overflow-hidden rounded-2xl p-6 flex items-center justify-between text-white h-full"
      style={{ background: grad }}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6 }}
      whileHover={{ y: -4 }}
    >
      <span className="inline-flex items-center gap-3 font-semibold text-lg">{icon}{label}</span>
      <ArrowUpRight size={18} className="opacity-80 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
    </motion.a>
  );
}
